import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ProgramCard from '../programs/ProgramCard';

interface Program {
    id: string;
    title: string;
    subtitle: string;
    content: string;
    contentEn: string;
    cover: string;
    status: string;
    category: string;
    publishDate: any; // Firestore timestamp
    teacher: any[]; // 教师引用
}

const TeacherAttendance: React.FC = () => {
    const [activePrograms, setActivePrograms] = useState<Program[]>([]);
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const getDescription = (content: string) => {
        return content.replace(/<[^>]+>/g, '').slice(0, 100);
    };

    // 获取所有活跃的项目
    useEffect(() => {
        const fetchActivePrograms = async () => {
            try {
                setLoading(true);
                const q = query(
                    collection(db, 'programs'),
                    where('status', '==', 'active')
                );
                const querySnapshot = await getDocs(q);
                
                const programsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Program[];
                
                setActivePrograms(programsData);
                setLoading(false);
            } catch (error) {
                console.error('获取活跃项目失败:', error);
                setLoading(false);
            }
        };

        fetchActivePrograms();
    }, []);

    // 处理项目卡片点击
    const handleProgramClick = (programId: string) => {
        navigate(`/teacher-attendance-detail?programId=${programId}`);
    };

    return (
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6">{i18n.language === 'zh' ? '教师考勤记录' : 'Teacher Attendance Record'}</h2>
            
            {loading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                </div>
            ) : activePrograms.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activePrograms.map(program => (
                        <div key={program.id} onClick={() => handleProgramClick(program.id)} className="cursor-pointer">
                            <ProgramCard 
                                id={program.id}
                                type="program"
                                title={program.title}
                                subtitle={program.subtitle || program.title}
                                description={getDescription(program.content)}
                                descriptionEn={getDescription(program.contentEn)}
                                image={program.cover}
                                category={program.category}
                                status="active"
                                disableNav={true}
                                extraInfo={{
                                    icon: 'calendar',
                                    text: `开始日期: ${program.publishDate?.toDate ? new Date(program.publishDate.toDate()).toLocaleDateString() : '未设置'}`,
                                    textEn: `Start Date: ${program.publishDate?.toDate ? new Date(program.publishDate.toDate()).toLocaleDateString() : 'Not set'}`
                                }}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-8">
                    <p className="text-blue-100">{t('attendance.noActivePrograms')}</p>
                </div>
            )}
        </div>
    );
};

export default TeacherAttendance; 